import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import {
  IBriefInfluencerSearchRequestV2,
  IGetBriefInfluencersV2Response,
} from "@shared/lib/types/briefInfluencerV2";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY } from "../constants";
import { BriefService } from "../services/brief.service";

export function useGetBriefInfluencersV2(
  params: IBriefInfluencerSearchRequestV2,
  enabled: boolean = true
) {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    BackednResponse<IGetBriefInfluencersV2Response>,
    Error,
    InfiniteData<BackednResponse<IGetBriefInfluencersV2Response>>,
    (string | IBriefInfluencerSearchRequestV2)[],
    number
  >({
    queryKey: [...BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY, params],
    queryFn: ({ pageParam = 1 }) =>
      BriefService.getBriefInfluencersV2({
        ...params,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.data?.pagination?.hasNextPage) {
        return lastPage.data.pagination.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!params.briefId && enabled,
  });

  const errorMessage = isError && error?.message;

  useEffect(() => {
    if (errorMessage) {
      ToastService.error(errorMessage);
    }
  }, [errorMessage]);

  // Flatten all pages into a single array
  const briefInfluencers = useMemo(() => {
    if (!data?.pages) return undefined;
    return {
      data: data.pages.flatMap(
        (page: BackednResponse<IGetBriefInfluencersV2Response>) =>
          page?.data?.data || []
      ),
      pagination: data.pages[data.pages.length - 1]?.data?.pagination,
    };
  }, [data]);

  return {
    briefInfluencers: briefInfluencers?.data,
    pagination: briefInfluencers?.pagination,
    isFetching: isLoading || isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    queryKey: [...BRIEF_INFLUENCER_V2_SEARCH_QUERY_KEY, params],
  };
}
